import type { Command } from 'commander';
import { Logger } from '@gitlab-org/logging';
import {
  AmbiguousPluginNameError,
  parsePluginScope,
  PluginNotInstalledError,
  PluginRegistry,
} from '@gitlab-org/duo-plugin-marketplace/node';
import type { DuoCommand, OptionGroup } from '../duo_command';
import { registerOptionsOnCommand } from '../../option_def';
import { ExitHandler } from '../../utils/exit';
import type { PluginContainerProvider } from './plugin_services';
import { describePlugin, formatNotInstalledMessage } from './describe_scope';
import { createScopeOptionDef } from './scope_option_def';

interface EnableOptions {
  scope: string;
}

const scopeOptionDef = createScopeOptionDef('Enable scope: user, project, or local.');

export class PluginEnableCommand implements DuoCommand {
  readonly name = 'enable <plugin>';

  readonly description = 'Enable an installed plugin.';

  readonly optionGroups: OptionGroup[] = [{ title: '`enable` options', options: [scopeOptionDef] }];

  readonly #containerProvider: PluginContainerProvider;

  constructor(containerProvider: PluginContainerProvider) {
    this.#containerProvider = containerProvider;
  }

  register(node: Command): void {
    registerOptionsOnCommand(node, [scopeOptionDef]);
    node.action(async (plugin: string, options: EnableOptions) => {
      try {
        const scope = parsePluginScope(options.scope);
        const workspacePath = (node.optsWithGlobals().cwd as string | undefined) ?? process.cwd();
        const registry = this.#containerProvider.getRequiredService(PluginRegistry, node);
        const result = await registry.enable(plugin, scope, workspacePath);
        // eslint-disable-next-line no-console
        console.log(`Enabled ${describePlugin(result.id, result.version, scope, workspacePath)}`);
      } catch (e) {
        const logger = this.#containerProvider.getRequiredService(Logger, node);
        logger.error('Failed to enable plugin', e instanceof Error ? e : new Error(String(e)));
        if (e instanceof PluginNotInstalledError) {
          // eslint-disable-next-line no-console
          console.error(formatNotInstalledMessage(e));
        } else if (e instanceof AmbiguousPluginNameError) {
          // eslint-disable-next-line no-console
          console.error(e.message);
        } else {
          const message = e instanceof Error ? e.message : String(e);
          // eslint-disable-next-line no-console
          console.error(`Failed to enable plugin: ${message}`);
        }
        await this.#containerProvider.getRequiredService(ExitHandler, node).exit(1);
      }
    });
  }
}
